/**
 * WuestPlumePage — Wüest 1992 double-plume bubble model for deep stratified lakes
 *
 * Inner plume = bubbles + carried water (buoyant). Outer plume = recirculating entrained
 * ambient water. At neutral buoyancy height the outer plume detrains laterally as a
 * gravity intrusion. Key Wüest formula bundle:
 *
 *     M_0  = g·(ρ_w − ρ_b)·Q_air / ρ_w               (initial buoyancy flux, m⁴/s³)
 *     N    = sqrt(−g/ρ · dρ/dz)                       (Brunt-Väisälä frequency, 1/s)
 *     h_d  = 4.7 · (M_0 / N³)^(1/4)                  (detrainment height above diffuser, m)
 *     Q_d  = 0.15 · M_0^(1/3) · h_d^(5/3)            (detrained volume flux, m³/s)
 *     OTE_above_cline = 1 − exp(−h_d / 8)            (fraction of O₂ released above pycnocline)
 *
 * Reference: Wüest, Brooks & Imboden (1992) Water Resources Research 28(12): 3235-3250, EAWAG.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Waves, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function WuestPlumePage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('wuest_double_plume');
  const [depth, setDepth] = useState(22);              // m total water column
  const [thermoclineDepth, setTd] = useState(8);       // m below surface
  const [N, setN] = useState(0.012);                   // 1/s Brunt-Väisälä
  const [Qair, setQair] = useState(2.5);               // L/s per diffuser at depth
  const [bubbleRho, setBubbleRho] = useState(1.2);     // kg/m³ at release depth (approx)

  const R = useMemo(() => {
    const g = 9.81;
    const rho_w = 1000;
    // Initial buoyancy flux (m⁴/s³). Q_air converted L/s → m³/s
    const M_0 = g * (rho_w - bubbleRho) / rho_w * (Qair / 1000);
    // Detrainment height (m above diffuser)
    const h_d = 4.7 * Math.pow(M_0 / Math.max(Math.pow(N, 3), 1e-9), 0.25);
    // Detrained volume flux
    const Q_d = 0.15 * Math.pow(M_0, 1 / 3) * Math.pow(h_d, 5 / 3);
    // Plume diameter at detrainment height (Wüest Fig 4)
    const diam = 0.34 * h_d;
    // Does the plume reach the surface?
    const reachesSurface = h_d > (depth - 0.5);  // diffuser ~0.5 m off bed
    // Fraction of O₂ released above pycnocline
    const heightAbovePyc = Math.max(0, h_d - (depth - thermoclineDepth));
    const oteAboveCline = 1 - Math.exp(-heightAbovePyc / 8);
    return { M_0, h_d, Q_d, diam, reachesSurface, oteAboveCline };
  }, [depth, thermoclineDepth, N, Qair, bubbleRho]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Waves} title="Module — Wüest Double-plume (deep stratified)"
        subtitle="EAWAG 1992: detraining bubble plumes in deep stratified reservoirs"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('wuest_double_plume')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="wuest_double_plume" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Buoyancy flux M₀</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.M_0 * 1e4, 1)}</div>
          <div className="text-xs text-slate-500 mt-1">×10⁻⁴ m⁴/s³</div>
        </div>
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-sky-300">Detrainment height h_d</div>
          <div className="text-2xl font-bold text-sky-200 mt-1">{fmt(R.h_d, 1)} m</div>
          <div className="text-xs text-slate-500 mt-1">{R.reachesSurface ? 'plume reaches surface' : 'plume stops below surface'}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Detrained flow Q_d</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.Q_d, 3)}</div>
          <div className="text-xs text-slate-500 mt-1">m³/s lateral intrusion</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">O₂ above pycnocline</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{(R.oteAboveCline * 100).toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">rest is hypolimnetic O₂ gain</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Total depth: <strong className="text-slate-100">{depth} m</strong></label>
          <input type="range" min={5} max={60} step={1} value={depth} onChange={e => setDepth(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Thermocline at: <strong className="text-slate-100">{thermoclineDepth} m</strong></label>
          <input type="range" min={2} max={30} step={0.5} value={thermoclineDepth} onChange={e => setTd(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">N (Brunt-Väisälä): <strong className="text-slate-100">{N.toFixed(3)} s⁻¹</strong></label>
          <input type="range" min={0.002} max={0.040} step={0.001} value={N} onChange={e => setN(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Air per diffuser: <strong className="text-slate-100">{Qair.toFixed(1)} L/s</strong></label>
          <input type="range" min={0.2} max={20} step={0.1} value={Qair} onChange={e => setQair(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Bubble ρ at depth: <strong className="text-slate-100">{bubbleRho.toFixed(1)} kg/m³</strong></label>
          <input type="range" min={1.0} max={6.0} step={0.1} value={bubbleRho} onChange={e => setBubbleRho(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Where this matters</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Deep stratified lakes (depth &gt; 15 m) where bubble plumes do <em>not</em> reach the surface — instead they detrain at the neutral-buoyancy height.</li>
          <li>The detraining outer plume forms a lateral gravity intrusion that delivers oxygenated water along the pycnocline.</li>
          <li>Plume diameter at detrainment ≈ 0.34 × h_d → useful for spacing adjacent diffusers to avoid plume overlap.</li>
          <li>When h_d &gt; H (total depth) the plume breaks through to the surface and the simpler Asaeda-Imberger model applies.</li>
        </ul>
        <p className="mt-2">For Barcombe (≈ 4-5 m mean depth) the plumes always reach the surface — this module would <strong>not</strong> normally be activated. It is provided for deep-storage analogues (e.g. Bewl Water, Ardingly) where mid-depth detrainment dominates.</p>
        <p className="italic text-slate-500 mt-2">Reference: Wüest, Brooks &amp; Imboden (1992) Water Resources Research 28(12): 3235-3250, EAWAG, Switzerland.</p>
      </div>
    </div>
  );
}
